import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, KeyRound, Navigation, BadgeIndianRupee, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { MaskedLines, Reveal, Kicker, BtnPrimary, BtnGhost, Marquee, Rich } from "@/components/Bits";
import { CATEGORIES } from "@/data/categories";

const WHY = [
  "Verified helpers — Aadhaar / PAN checks + address validation before activation.",
  "Nearby by default — GPS-first search shows the closest available helper.",
  "Transparent pricing — fixed per-hour, per-visit or negotiable rates shown up-front.",
  "Live tracking — see your Middle Man on the map, with real-time ETA.",
  "OTP-secured jobs — work starts and finishes only after the Customer shares an OTP.",
  "Pan-India coverage — 35 states, 720+ districts, 4,200+ talukas, 4,300+ cities.",
];

const STEPS = [
  { n: "01", title: "Post your work", body: "Choose a category, describe the task and share your location through the in-app Send Work flow." },
  { n: "02", title: "Match with a nearby Middle Man", body: "GPS-first search shows the closest available, verified helper for your job." },
  { n: "03", title: "OTP-secured start & finish", body: "Work starts and finishes only after you share an OTP — a permanent record of every job." },
  { n: "04", title: "Track live, then rate", body: "See your Middle Man on the map with real-time ETA, and rate the work when it's done." },
];

const MARQUEE = [
  "26 service categories",
  "208 subcategories",
  "Aadhaar / PAN verified helpers",
  "OTP-secured start & finish",
  "Live tracking with real-time ETA",
  "Transparent recommended charges",
  "Pan-India coverage",
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1617347454431-f49d7ff5c3b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBkZWxpdmVyeSUyMHJpZGVyJTIwc2Nvb3RlcnxlbnwwfHx8fDE3ODg2MzQ0Njl8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <div data-testid="home-page">
      <SEO
        title=""
        description="Middle Man — verified local help for everyday tasks. Book trusted, Aadhaar/PAN-verified Middle Men near you for errands, deliveries, elderly care, government work and more. Operated by Tourist Junction Private Limited."
        path="/"
      />

      {/* HERO */}
      <section className="overflow-hidden border-b border-line">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-20 pt-32 md:pt-44 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <Reveal><Kicker>Middle Man</Kicker></Reveal>
            <MaskedLines
              className="mt-7 font-serif text-[13vw] font-semibold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
              lines={[
                "Verified local help",
                <>for <em className="text-brand">everyday</em> tasks.</>,
              ]}
            />
            <Reveal delay={0.35}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-mute md:text-lg">
                From medicine pickup and grocery runs to farm labour, government paperwork and elderly care —
                book a trusted Middle Man near you in minutes. Live tracking. Fair pricing. Fully verified.
              </p>
            </Reveal>
            <Reveal delay={0.45}>
              <div className="mt-9 flex flex-wrap gap-4">
                <BtnPrimary to="/services" testid="hero-explore-services-btn">Explore services</BtnPrimary>
                <BtnGhost to="/pricing" testid="hero-see-pricing-btn">See recommended charges</BtnGhost>
              </div>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-[13px] font-semibold text-ink">
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" /> Aadhaar / PAN verified</span>
                <span className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-brand" /> OTP-secured jobs</span>
                <span className="flex items-center gap-2"><Navigation className="h-4 w-4 text-brand" /> Live tracking</span>
              </div>
            </Reveal>
          </div>

          <motion.div ref={imgRef} style={{ y }} className="relative hidden lg:block" data-testid="hero-image">
            <div className="border border-line bg-white p-3 shadow-[8px_8px_0_0_#0F1115]">
              <img src={HERO_IMG} alt="A Middle Man rider on a scooter making a local delivery" className="h-[460px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 flex items-center gap-3 border border-line bg-cream px-5 py-4 shadow-[4px_4px_0_0_#E85A2C]">
              <img src="/middleman-icon.png" alt="" className="h-10 w-10 rounded-full object-cover" />
              <div>
                <p className="text-[13px] font-bold text-ink">Your nearby Middle Man</p>
                <p className="text-[11px] text-mute">Verified · Tracked · OTP-secured</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE} />

      {/* WHY */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <Reveal><Kicker>Why Middle Man</Kicker></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl font-semibold tracking-tight md:text-5xl">
            Built on trust, priced with honesty.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-x-14 gap-y-10 md:grid-cols-2" data-testid="why-grid">
          {WHY.map((w, i) => {
            const [lead, rest] = w.split(" — ");
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex gap-6 border-t border-ink/15 pt-6">
                  <span className="font-serif text-3xl font-semibold text-brand">0{i + 1}</span>
                  <p className="text-[15px] leading-relaxed text-mute">
                    <strong className="font-semibold text-ink">{lead}</strong>
                    {rest ? ` — ${rest}` : ""}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="border-y border-line bg-creamalt/60">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal><Kicker>What we do</Kicker></Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-6 font-serif text-3xl font-semibold tracking-tight md:text-5xl">
                  26 categories. <em className="text-brand">208</em> ways we help.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link to="/services" data-testid="home-all-services-link" className="group flex items-center gap-2 text-sm font-bold text-brand">
                View all categories
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-services-grid">
            {CATEGORIES.slice(0, 8).map((c, i) => (
              <Reveal key={c.id} delay={i * 0.04} className={i === 0 ? "sm:col-span-2" : ""}>
                <Link
                  to="/services"
                  data-testid={`home-cat-${c.id}`}
                  className="group flex h-full flex-col justify-between border border-line bg-white p-6 transition-colors duration-300 hover:border-brand"
                >
                  <div className="flex items-start justify-between">
                    <c.Icon className="h-6 w-6 text-brand" strokeWidth={1.6} />
                    <span className="font-mono text-[11px] text-mute">{c.sub_categories.length} subcategories</span>
                  </div>
                  <div className="mt-8">
                    <h3 className="font-serif text-lg font-semibold leading-snug text-ink group-hover:text-brand">{c.name}</h3>
                    <p className="mt-1 font-mono text-[12px] text-mute">
                      {c.price_max != null ? `₹${c.price_min}–${c.price_max} ${c.price_unit_label}` : "Charges mutually decided"}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <Reveal><Kicker>How it works</Kicker></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl font-semibold tracking-tight md:text-5xl">
            Four steps between you and a finished task.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4" data-testid="home-steps">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.07}>
              <div className="border-l-2 border-brand pl-6">
                <p className="font-serif text-5xl font-semibold text-ink/15">{s.n}</p>
                <h3 className="mt-4 font-serif text-xl font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-mute">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14">
          <BtnGhost to="/how-it-works" testid="home-how-it-works-btn">See the full flow</BtnGhost>
        </Reveal>
      </section>

      {/* JOIN CTA */}
      <section className="bg-ink text-cream">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 md:py-28 lg:grid-cols-2">
          <div>
            <Reveal><Kicker light>For Middle Men</Kicker></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-serif text-3xl font-semibold tracking-tight md:text-5xl">
                Your skills. Your city. <em className="text-brand">Your income.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-cream/65">
                Join as an independent, verified service provider. Set your own categories, work radius and pricing —
                and receive job requests from customers near you.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-9 flex flex-wrap gap-4">
                <BtnPrimary to="/join" testid="home-join-btn">Join as a Middle Man</BtnPrimary>
                <Link to="/for-customers" data-testid="home-for-customers-link" className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-7 py-3.5 text-sm font-bold text-cream transition-colors duration-300 hover:border-brand hover:text-brand">
                  For customers
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="hidden lg:block">
            <div className="border border-cream/15 bg-ink p-3 shadow-[8px_8px_0_0_#E85A2C]">
              <img
                src="https://images.unsplash.com/photo-1572195577046-2f25894c06fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBkZWxpdmVyeSUyMHJpZGVyJTIwc2Nvb3RlcnxlbnwwfHx8fDE3ODg2MzQ0Njl8MA&ixlib=rb-4.1.0&q=85"
                alt="A Middle Man riding a scooter to deliver goods"
                className="h-[380px] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRAND STRIP */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-6 py-16 text-left md:py-20">
          <Reveal>
            <div className="border border-line bg-creamalt p-8 md:p-12" data-testid="brand-strip">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand">The company behind the brand</p>
              <p className="mt-4 font-serif text-2xl font-semibold tracking-tight md:text-3xl">
                Middle Man is a brand operated by Tourist Junction Private Limited.
              </p>
              <p className="mt-3 text-sm text-mute">
                Registered office · Narhe (N.V.), Pune 411041, Maharashtra, India ·{" "}
                <a href="mailto:info.touristjunction@gmail.com" className="font-semibold text-brand">info.touristjunction@gmail.com</a>
                {" · "}+91 96070 14213
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
